import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, doc, getDoc } from "firebase/firestore";
import { Table, Paper, TableHead, TableRow, TableCell, TableBody, TextField, TableContainer, Typography, Button, Box, Tooltip, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import { StyledTableCell, StyledTableRow } from '../../utils/styled';
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import moment from "moment";
import "moment/locale/es";
import { motivos } from "../../utils/default";

const currentUser = JSON.parse(localStorage.getItem("currentUser"));
const firestore = getFirestore();

const getMotivoColor = (resumen) => {
    switch (resumen) {
        case 'MR': return { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' }; // Movil Roto
        case 'L': return { bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' }; // Libre
        case 'E': return { bg: '#f3e5f5', color: '#7b1fa2', border: '#ce93d8' }; // Enfermedad
        case 'S': return { bg: '#ffebee', color: '#c62828', border: '#ef9a9a' }; // Suspensión
        case 'A': return { bg: '#ffebee', color: '#b71c1c', border: '#ef9a9a' }; // Ausente
        case 'ST': return { bg: '#eceff1', color: '#455a64', border: '#b0bec5' }; // Sin Tareas
        case 'PE': return { bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' }; // Permiso Especial
        case 'B': return { bg: '#efebe9', color: '#4e342e', border: '#bcaaa4' }; // Bloqueado
        case 'PC': return { bg: '#e0f7fa', color: '#00695c', border: '#80deea' }; // Capacitación
        default: return { bg: '#f5f5f5', color: '#424242', border: '#e0e0e0' }; // Otros
    }
};

const getResumenAusencias = (diasNoAsistidos = {}) => {
    const conteo = {};
    Object.entries(diasNoAsistidos).forEach(([dia, info]) => {
        if (!info || !info.motivo) return;
        const cod = info.motivo;
        if (cod === 'T') return;
        if (!conteo[cod]) {
            const m = motivos.find(item => item.resumen === cod);
            conteo[cod] = {
                count: 0,
                dias: [],
                nombre: m ? m.motivo : cod
            };
        }
        conteo[cod].count += 1;
        conteo[cod].dias.push(dia);
    });
    return conteo;
};

const ListAsistencias = () => {
    const [mesSeleccionado, setMesSeleccionado] = useState(moment());
    const [asistencias, setAsistencias] = useState(null);
    const [diasObjetivo, setDiasObjetivo] = useState(0);
    const [cuadrillasFiltro, setCuadrillasFiltro] = useState([]);
    const [feriados, setFeriados] = useState({});
    const [inasistenciaModal, setInasistenciaModal] = useState({ open: false, cuadrilla: '', dia: null, motivo: '', nota: '' });

    useEffect(() => {
        const cargarAsistencias = async () => {
            setAsistencias(null);
            try {
                const year = mesSeleccionado.year();
                const month = mesSeleccionado.format("MM");
                const asistenciasRef = collection(firestore, `asistencias/${year}/${month}`);

                const snapshot = await getDocs(asistenciasRef);
                const data = snapshot.docs
                    .filter(doc => doc.id !== "dias")
                    .map(doc => ({
                        idCuadrilla: doc.id,
                        ...doc.data()
                    }))
                    .filter(cuadrilla => {
                        const id = cuadrilla.idCuadrilla;
                        return id !== "0" && id !== "1" && id !== 0 && id !== 1;
                    })
                    .sort((a, b) => Number(a.idCuadrilla) - Number(b.idCuadrilla));

                setAsistencias(data);

                // Obtener el documento "dias" para extraer dias_objetivo
                const diasDocRef = doc(firestore, `asistencias/${year}/${month}/dias`);
                const diasDocSnap = await getDoc(diasDocRef);
                if (diasDocSnap.exists()) {
                    const data = diasDocSnap.data();
                    setDiasObjetivo(data.dias_objetivo);
                    setFeriados(data.feriados || {});
                } else {
                    setDiasObjetivo(null);
                    setFeriados({});
                }
            } catch (err) {
                console.error("Error loading assistance:", err);
                setAsistencias([]);
            }
        };

        cargarAsistencias();
    }, [mesSeleccionado]);

    return (
        <div>
            <h2>Listado de Asistencias</h2>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ width: '100%', padding: 2, gap: 2 }}>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <Autocomplete
                        multiple
                        size="small"
                        options={asistencias || []}
                        value={cuadrillasFiltro}
                        onChange={(_, value) => setCuadrillasFiltro(value)}
                        getOptionLabel={(option) => option.usuarioCuadrilla
                            ? `${option.usuarioCuadrilla} - ${option.nroCuadrilla}`
                            : String(option.nroCuadrilla ?? option.idCuadrilla)}
                        isOptionEqualToValue={(option, value) => option.idCuadrilla === value.idCuadrilla}
                        sx={{ width: '100%', maxWidth: 360 }}
                        renderInput={(params) => (
                            <TextField {...params} label="Filtrar cuadrillas" placeholder="Cuadrillas" />
                        )}
                    />
                </Box>
                <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                    <TextField
                        type="month"
                        value={mesSeleccionado.format("YYYY-MM")}
                        onChange={(e) => setMesSeleccionado(moment(e.target.value))}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" color="text.secondary">
                        Días objetivo: <strong>{diasObjetivo ?? "N/A"}</strong>
                    </Typography>
                </Box>
                {(currentUser?.rol === 'admin' || currentUser?.rol === 'write') && (
                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
                        <Button variant="contained" href="/asistencia">
                            Cargar Asistencia
                        </Button>
                    </Box>
                )}
            </Box>

            <TableContainer
                    component={Paper}
                sx={{
                    maxHeight: '70vh',
                    overflow: 'auto',
                    '& .MuiTableCell-root': {
                        borderRight: '1px solid rgba(224, 224, 224, 0.5)',
                        padding: '2px 4px',
                    },
                    '& .MuiTableCell-root:last-child': {
                        borderRight: 'none'
                    }
                }}
            >
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell
                                sx={{
                                    position: 'sticky',
                                    left: 0,
                                    zIndex: 1001,
                                    minWidth: '180px',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                Cuadrilla
                            </StyledTableCell>
                            {Array.from({ length: mesSeleccionado?.daysInMonth() }, (_, i) => {
                                const dayNum = i + 1;
                                const fecha = mesSeleccionado.clone().date(dayNum).locale('es');
                                const diaSemana = fecha.format('dd');
                                const diaCapitalizado = diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1);
                                const esDomingo = fecha.day() === 0;
                                const esFinde = esDomingo || fecha.day() === 6;
                                const esFeriado = !!feriados[dayNum];
                                const headerSx = esFeriado
                                    ? { backgroundColor: '#c9b501 !important', color: '#827717' }
                                    : esDomingo
                                        ? { backgroundColor: '#f6882d !important', color: '#e65100' }
                                        : {};
                                return (
                                    <Tooltip key={i} title={esFeriado ? `Feriado: ${feriados[dayNum]}` : ''} arrow disableHoverListener={!esFeriado}>
                                        <StyledTableCell
                                            align="center"
                                            sx={{
                                                minWidth: '36px',
                                                width: '36px',
                                                padding: '2px 4px',
                                                lineHeight: 1,
                                                ...headerSx,
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                                                <span style={{ fontSize: '0.6rem', opacity: 0.75, fontWeight: esFinde || esFeriado ? 700 : 400 }}>
                                                    {diaCapitalizado}
                                                </span>
                                                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>
                                                    {dayNum}
                                                </span>
                                            </div>
                                        </StyledTableCell>
                                    </Tooltip>
                                );
                            })}
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Días Activos</StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap', minWidth: '110px' }}>Ausencias</StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>% Bruto</StyledTableCell>
                            <StyledTableCell align="center" sx={{ whiteSpace: 'nowrap' }}>% Neto</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(cuadrillasFiltro.length > 0
                            ? asistencias?.filter(a => cuadrillasFiltro.some(f => f.idCuadrilla === a.idCuadrilla))
                            : asistencias
                        )?.length > 0 ? (
                            (cuadrillasFiltro.length > 0
                                ? asistencias.filter(a => cuadrillasFiltro.some(f => f.idCuadrilla === a.idCuadrilla))
                                : asistencias
                            ).map((cuadrilla, index) => {
                            const totalDiasNoAsistidos = Object.keys(cuadrilla.dias_no_asistidos || {}).length;
                            const totalDiasAsistidos = mesSeleccionado.daysInMonth() - totalDiasNoAsistidos;
                            const percentilBruto = ((totalDiasAsistidos / diasObjetivo) * 100).toFixed(2);
                            const percentilNeto = percentilBruto >= 100 ? 50 : (totalDiasAsistidos * 0.5).toFixed(2);
                            const ausencias = getResumenAusencias(cuadrilla.dias_no_asistidos);
                            const codigosAusencia = Object.keys(ausencias);

                            return (
                                <StyledTableRow key={index} sx={{ height: '32px' }}>
                                    <StyledTableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            position: 'sticky',
                                            left: 0,
                                            zIndex: 1000,
                                            minWidth: '180px',
                                            backgroundColor: '#ffffff',
                                            whiteSpace: 'nowrap',
                                            fontSize: '0.7rem',
                                        }}
                                    >
                                        {cuadrilla.usuarioCuadrilla ? `${cuadrilla.usuarioCuadrilla} - ${cuadrilla.nroCuadrilla}` : cuadrilla.nroCuadrilla}
                                    </StyledTableCell>
                                    {Array.from({ length: mesSeleccionado?.daysInMonth() }, (_, i) => {
                                        const dayNum = i + 1;
                                        const dia = cuadrilla.dias_no_asistidos?.[dayNum];
                                        const esBackup = cuadrilla.dias_backup?.[dayNum];
                                        const tieneNota = dia && dia.nota;
                                        const esDomingo = mesSeleccionado.clone().date(dayNum).day() === 0;
                                        const esFeriado = !!feriados[dayNum];
                                        const esTrabajadoDomingoFeriado = dia && dia.motivo === 'T';
                                        const bg = esBackup ? "#c8e6c9"
                                            : (esFeriado && !esTrabajadoDomingoFeriado) ? "#fff176"
                                            : (esDomingo && !esTrabajadoDomingoFeriado) ? "#ffcc80"
                                            : (dia && !esTrabajadoDomingoFeriado) ? "#ffebee"
                                            : "inherit";

                                        return (
                                            <StyledTableCell
                                                key={i}
                                                align="center"
                                                sx={{
                                                    backgroundColor: bg,
                                                    minWidth: '36px',
                                                    width: '36px',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    cursor: dia ? 'pointer' : 'default',
                                                    '&:hover': dia ? { filter: 'brightness(0.92)' } : {},
                                                }}
                                                onClick={() => {
                                                    if (!dia) return;
                                                    setInasistenciaModal({
                                                        open: true,
                                                        cuadrilla: cuadrilla.usuarioCuadrilla
                                                            ? `${cuadrilla.usuarioCuadrilla} - ${cuadrilla.nroCuadrilla}`
                                                            : String(cuadrilla.nroCuadrilla),
                                                        dia: dayNum,
                                                        motivo: dia.motivo,
                                                        nota: dia.nota || '',
                                                    });
                                                }}
                                            >
                                                {dia ? (
                                                    <span style={{ fontSize: '0.7rem' }}>
                                                        {dia.motivo === 'T' ? '✔' : dia.motivo}
                                                    </span>
                                                ) : esBackup && <span style={{ fontSize: '0.7rem' }}>B</span>}
                                            </StyledTableCell>
                                        );
                                    })}
                                    <StyledTableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                        {totalDiasAsistidos}
                                    </StyledTableCell>
                                    <StyledTableCell align="center" sx={{ padding: '2px 6px', minWidth: '110px' }}>
                                        {codigosAusencia.length > 0 ? (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '3px', justifyContent: 'center', alignItems: 'center' }}>
                                                {codigosAusencia.map(cod => {
                                                    const item = ausencias[cod];
                                                    const colors = getMotivoColor(cod);
                                                    return (
                                                        <Tooltip
                                                            key={cod}
                                                            title={`${item.nombre} (${cod}): ${item.count} día(s) [Día(s): ${item.dias.join(', ')}]`}
                                                            arrow
                                                        >
                                                            <Box
                                                                component="span"
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    backgroundColor: colors.bg,
                                                                    color: colors.color,
                                                                    border: `1px solid ${colors.border}`,
                                                                    borderRadius: '8px',
                                                                    px: '5px',
                                                                    py: '0.5px',
                                                                    fontSize: '0.65rem',
                                                                    fontWeight: 700,
                                                                    lineHeight: 1.2,
                                                                    cursor: 'default',
                                                                    whiteSpace: 'nowrap',
                                                                    transition: 'all 0.15s ease',
                                                                    '&:hover': {
                                                                        filter: 'brightness(0.92)',
                                                                        transform: 'scale(1.05)'
                                                                    }
                                                                }}
                                                            >
                                                                {cod}: {item.count}
                                                            </Box>
                                                        </Tooltip>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <span style={{ color: '#aaa', fontSize: '0.75rem' }}>-</span>
                                        )}
                                    </StyledTableCell>
                                    <StyledTableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                        {percentilBruto}%
                                    </StyledTableCell>
                                    <StyledTableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}>
                                        {percentilNeto}%
                                    </StyledTableCell>
                                </StyledTableRow>
                            );
                        })
                        ) : (
                            <StyledTableRow>
                                <StyledTableCell colSpan={50} align="center">
                                    {asistencias?.length === 0 ? (
                                        <Typography variant="h5">No se encontraron resultados</Typography>
                                    ) : (
                                        <CircularProgress />
                                    )}
                                </StyledTableCell>
                            </StyledTableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={inasistenciaModal.open}
                onClose={() => setInasistenciaModal(prev => ({ ...prev, open: false }))}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Inasistencia — Día {inasistenciaModal.dia}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Cuadrilla
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {inasistenciaModal.cuadrilla}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Motivo
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        {(() => {
                            const m = motivos.find(x => x.resumen === inasistenciaModal.motivo);
                            return m ? `${m.resumen} — ${m.motivo}` : inasistenciaModal.motivo;
                        })()}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Nota
                    </Typography>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', fontStyle: inasistenciaModal.nota ? 'normal' : 'italic', color: inasistenciaModal.nota ? 'inherit' : 'text.disabled' }}>
                        {inasistenciaModal.nota || 'Sin nota'}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setInasistenciaModal(prev => ({ ...prev, open: false }))}>
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default ListAsistencias;
